import React, { useState, useEffect } from 'react';
import TestListScreen from './common/TestList';
import TestQuestionsScreen from './common/TestQuestions';
import TestResultsScreen from './common/TestResults';
import { useQuery } from '@tanstack/react-query';

 const fetchTests = async () => {
  const response = await fetch('https://question-manager-backend.onrender.com/api/student/tests/available-with-questions');
  if (!response.ok) {
    throw new Error('فشل في جلب البيانات');
  }
  return response.json();
};

 const extractTestData = (test) => {
  if (!test) return null;
  
  return {
    id: test.id,
    title: test.title,
    description: test.description,
    className: test.className,
    totalLevels: test.totalLevels,
    hearts: test.hearts ? { 
      total: test.hearts.total || 3, 
      remaining: test.hearts.remaining || 3 
    } : { total: 3, remaining: 3 },
    hints: test.hints ? { 
      total: test.hints.total || 2, 
      remaining: test.hints.remaining || 2 
    } : { total: 2, remaining: 2 },
    statistics: test.statistics ? { 
      totalQuestions: test.statistics.totalQuestions || 0,
      totalPoints: test.statistics.totalPoints || 0,
      averagePointsPerQuestion: test.statistics.averagePointsPerQuestion || 1
    } : { totalQuestions: 0, totalPoints: 0, averagePointsPerQuestion: 1 },
    status: test.status,
    progress: test.progress,
    bgColor: test.bgColor,
    icon: test.icon,
    levels: Array.isArray(test.levels) ? test.levels.map(level => ({
      levelNumber: level.levelNumber,
      levelTitle: level.levelTitle || `المستوى ${level.levelNumber}`,
      numberOfQuestions: level.numberOfQuestions || 0,
      questions: Array.isArray(level.questions) ? level.questions.map(question => ({
        id: question.id || Math.random().toString(),
        questionText: question.questionText || 'السؤال غير متوفر',
        options: Array.isArray(question.options) ? [...question.options] : ['غير متوفر'],
        correctAnswer: question.correctAnswer || '',
        explanation: question.explanation || '',
        points: question.points || 1,
        questionType: question.questionType || 'text-only',
        difficulty: question.difficulty || 'medium',
        level: question.level || 1,
        questionImage: question.questionImage,
        optionsImages: Array.isArray(question.optionsImages) ? [...question.optionsImages] : []
      })) : []
    })) : [],
    allQuestions: Array.isArray(test.allQuestions) ? test.allQuestions.map(question => ({
      id: question.id || Math.random().toString(),
      questionText: question.questionText || 'السؤال غير متوفر',
      options: Array.isArray(question.options) ? [...question.options] : ['غير متوفر'],
      correctAnswer: question.correctAnswer || '',
      explanation: question.explanation || '',
      points: question.points || 1,
      questionType: question.questionType || 'text-only',
      difficulty: question.difficulty || 'medium',
      level: question.level || 1,
      questionImage: question.questionImage,
      optionsImages: Array.isArray(question.optionsImages) ? [...question.optionsImages] : []
    })) : []
  };
};

 const extractAnswersData = (answers) => {
  if (!answers) return [];
  
  return answers.map(answer => ({
    selectedIndex: answer.selectedIndex,
    isCorrect: answer.isCorrect,
    timeSpent: answer.timeSpent,
    timeUp: answer.timeUp
  }));
};

 const loadSavedResults = () => {
  try {
    const savedTest = localStorage.getItem('testResults_currentTest');
    const savedAnswers = localStorage.getItem('testResults_userAnswers');
    
    if (savedTest && savedAnswers) {
      const parsedTest = JSON.parse(savedTest);
      const parsedAnswers = JSON.parse(savedAnswers);
      
       const timestamp = localStorage.getItem('testResults_timestamp');
      if (timestamp) {
        const age = Date.now() - parseInt(timestamp);
        if (age < 60 * 60 * 1000) { // أقل من ساعة
          return {
            test: parsedTest,
            answers: parsedAnswers
          };
        } else {
           localStorage.removeItem('testResults_currentTest');
          localStorage.removeItem('testResults_userAnswers');
          localStorage.removeItem('testResults_timestamp');
          localStorage.removeItem('testResults_redirectPath');
          localStorage.removeItem('hasSavedTestResults');
        }
      }
    }
  } catch (error) {
    console.error('Error loading saved results:', error);
     localStorage.removeItem('testResults_currentTest');
    localStorage.removeItem('testResults_userAnswers');
    localStorage.removeItem('testResults_timestamp');
    localStorage.removeItem('testResults_redirectPath');
    localStorage.removeItem('hasSavedTestResults');
  }
  
  return null;
};

const TestPage = () => {
  const [currentScreen, setCurrentScreen] = useState('list');
  const [currentTest, setCurrentTest] = useState(null);
  const [userAnswers, setUserAnswers] = useState([]);
  const [availableTests, setAvailableTests] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);

   const { data, isLoading, error } = useQuery({
    queryKey: ['tests'],
    queryFn: fetchTests,
    staleTime: 5 * 60 * 1000,
  });

   useEffect(() => {
    if (data && data.success && Array.isArray(data.tests)) {
      setAvailableTests(data.tests);
    }
  }, [data]);

   useEffect(() => {
    const shouldReturnToResults = localStorage.getItem('returnToResults');
    
    if (shouldReturnToResults === 'true') {
      console.log('يجب العودة إلى النتائج بعد تسجيل الدخول');
      setLoadingResults(true);
      
       const savedData = loadSavedResults();
      
      if (savedData) {
        console.log('تم تحميل النتائج المحفوظة');
        setCurrentTest(savedData.test);
        setUserAnswers(savedData.answers);
        setCurrentScreen('results');
        
         localStorage.setItem('hasSavedTestResults', 'true');
        localStorage.setItem('testResults_redirectPath', '/tests');
      } else {
        console.log('لم يتم العثور على نتائج محفوظة');
      }
      
      setLoadingResults(false);
      localStorage.removeItem('returnToResults');
    }
  }, []);

   useEffect(() => {
    const hasSavedResults = localStorage.getItem('hasSavedTestResults');
    if (hasSavedResults === 'true') {
      const savedData = loadSavedResults();
      if (savedData && currentScreen === 'list') {
        console.log('استعادة النتائج المحفوظة عند التحميل الأولي');
        setCurrentTest(savedData.test);
        setUserAnswers(savedData.answers);
        setCurrentScreen('results');
      }
    }
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleStartTest = (test) => {
    if (!test) {
      console.error('No test provided to handleStartTest');
      return;
    }

    try {
       const testData = extractTestData(test);
      
      if (!testData) {
        throw new Error('Failed to extract test data');
      }

       const hasQuestionsInLevels = testData.levels.some(level => 
        level.questions && level.questions.length > 0
      );
      
      const hasAllQuestions = testData.allQuestions && testData.allQuestions.length > 0;

      if (!hasQuestionsInLevels && !hasAllQuestions) {
        alert('هذا الاختبار لا يحتوي على أسئلة حالياً. الرجاء اختيار اختبار آخر.');
        return;
      }

      setCurrentTest(testData);
      setCurrentScreen('questions');
      setUserAnswers([]);
      
       localStorage.setItem('currentTest', JSON.stringify(testData));
      localStorage.removeItem('userAnswers');
      
       localStorage.removeItem('testResults_currentTest');
      localStorage.removeItem('testResults_userAnswers');
      localStorage.removeItem('testResults_timestamp');
      localStorage.removeItem('testResults_redirectPath');
      localStorage.removeItem('hasSavedTestResults');
      localStorage.removeItem('returnToResults');
      
    } catch (error) {
      console.error('Error starting test:', error);
     }
  };

  const handleTestComplete = (answers) => {
    if (!Array.isArray(answers)) {
      console.error('Invalid answers provided to handleTestComplete');
      return;
    }
    
    setUserAnswers(answers);
    setCurrentScreen('results');
    
     try {
      localStorage.setItem('userAnswers', JSON.stringify(answers));
      
       const testData = extractTestData(currentTest);
      const answersData = extractAnswersData(answers);
      
      localStorage.setItem('testResults_currentTest', JSON.stringify(testData));
      localStorage.setItem('testResults_userAnswers', JSON.stringify(answersData));
      localStorage.setItem('testResults_timestamp', Date.now().toString());
      localStorage.setItem('testResults_redirectPath', '/tests');
      localStorage.setItem('hasSavedTestResults', 'true');
      
      console.log('تم حفظ النتائج للاسترجاع بعد تسجيل الدخول');
    } catch (error) {
      console.error('Error saving answers:', error);
    }
  };

  const handleRestartTest = () => {
    setCurrentScreen('list');
    setCurrentTest(null);
    setUserAnswers([]);
    
     localStorage.removeItem('currentTest');
    localStorage.removeItem('userAnswers');
    localStorage.removeItem('testResults_currentTest');
    localStorage.removeItem('testResults_userAnswers');
    localStorage.removeItem('testResults_timestamp');
    localStorage.removeItem('testResults_redirectPath');
    localStorage.removeItem('hasSavedTestResults');
    localStorage.removeItem('returnToResults');
  };

   const handleBackToList = () => {
    setCurrentScreen('list');
   };

  const renderCurrentScreen = () => {
    switch (currentScreen) {
      case 'list':
        return (
          <TestListScreen 
            onStartTest={handleStartTest}
            scrollToSection={scrollToSection}
            tests={availableTests}
            isLoading={isLoading}
            error={error}
          />
        );
      case 'questions':
        return currentTest ? (
          <TestQuestionsScreen
            currentTest={currentTest}
            onTestComplete={handleTestComplete}
            onRestartTest={handleRestartTest}
            scrollToSection={scrollToSection}
          />
        ) : (
          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-center">
              <p className="text-gray-600">لم يتم تحميل بيانات الاختبار</p>
              <button 
                onClick={handleRestartTest}
                className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg"
              >
                العودة للقائمة
              </button>
            </div>
          </div>
        );
      case 'results':
        return currentTest ? (
          <TestResultsScreen
            currentTest={currentTest}
            userAnswers={userAnswers}
            onRestartTest={handleRestartTest}
            onBackToList={handleBackToList}
            scrollToSection={scrollToSection}
          />
        ) : loadingResults ? (
          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">جاري تحميل النتائج...</p>
            </div>
          </div>
        ) : (
          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-center">
              <p className="text-gray-600">لا توجد نتائج للعرض</p>
              <button 
                onClick={handleRestartTest}
                className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg"
              >
                العودة للقائمة
              </button>
            </div>
          </div>
        );
      default:
        return (
          <TestListScreen 
            onStartTest={handleStartTest}
            scrollToSection={scrollToSection}
            tests={availableTests}
            isLoading={isLoading}
            error={error}
          />
        );
    }
  };

  return renderCurrentScreen();
};

export default TestPage;

